import { HintAnimation } from '../../portal-hints/hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_H,
  DIVIDER_V,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbi3d,
  PRESSED,
  Refs,
  renderCamera,
  renderCursor,
  stage,
} from '../../portal-hints/animations/scene-kit';

/**
 * Academy-Filmszenen als Daten: HTML aus den Bausteinen der Portalhinweise
 * (`scene-kit.ts`) plus Keyframe-Spuren. Eine Szene ist ein `HintAnimation`
 * wie die Hinweis-Filme — `app-academy-film` spielt Hinweis-Film und
 * Szenen hintereinander. Bühne 640×354, Zeit in Sekunden.
 *
 * Spuren: `at` = Zeitpunkte, je Eigenschaft gleich viele Werte; dazwischen
 * `MOTION.path` (ease-in-out), vor dem ersten / nach dem letzten Punkt der
 * erste / letzte Wert. Eine Spur gilt für alle Elemente mit `data-r=<r>`.
 * Vorgaben: features/prd/academy-content.md › Filmvorgaben.
 */

export interface Track {
  r: string;
  at: number[];
  /** Deckkraft 0…1 */
  o?: number[];
  /** Verschiebung in px */
  x?: number[];
  y?: number[];
  /** Skalierung */
  s?: number[];
}

export interface SceneSpec {
  seconds: number;
  /** Standbild bei `prefers-reduced-motion` — muss die Szene allein erklären. */
  still: number;
  /** Inhalt der Kamera-Ebene (640×400). */
  html: string;
  tracks?: Track[];
  cursor?: { at: number[]; x: number[]; y: number[]; clicks?: number[] };
  camera?: { at: number[]; x: number[]; y: number[]; s: number[] };
}

export class StoryScene implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds: number;
  readonly stillAt: number;
  private refs: Refs = {};

  constructor(private spec: SceneSpec) {
    this.playbackSeconds = spec.seconds;
    this.stillAt = spec.still;
  }

  mount(host: HTMLElement): void {
    const cursor = this.spec.cursor ? cursorTemplate(nextUid('al-academy')) : '';
    this.refs = mountTemplate(host, stage(this.spec.html + cursor));
  }

  render(t: number): void {
    const { camera, cursor, tracks = [] } = this.spec;
    if (camera) {
      renderCamera(
        this.refs['camera'][0],
        MOTION.path(camera.at, camera.x, t),
        MOTION.path(camera.at, camera.y, t),
        MOTION.path(camera.at, camera.s, t),
      );
    } else {
      renderCamera(this.refs['camera'][0], 320, 176, 1);
    }
    // Weicher Loop: kurz ein- und ausblenden statt hart auf den Anfang zu springen.
    const fade = Math.min(MOTION.enter(0, 0.35, t), 1 - MOTION.glide(this.spec.seconds - 0.5, this.spec.seconds, t));
    this.refs['camera'][0].style.opacity = String(fade);

    for (const tr of tracks) {
      const els = this.refs[tr.r];
      if (!els) continue;
      const o = tr.o ? MOTION.path(tr.at, tr.o, t) : null;
      const x = tr.x ? MOTION.path(tr.at, tr.x, t) : 0;
      const y = tr.y ? MOTION.path(tr.at, tr.y, t) : 0;
      const s = tr.s ? MOTION.path(tr.at, tr.s, t) : 1;
      for (const el of els) {
        if (o !== null) el.style.opacity = String(o);
        if (tr.x || tr.y || tr.s) el.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
      }
    }

    if (cursor) {
      const clicks = (cursor.clicks ?? []).map((at) => click(at, t));
      const press = Math.max(0, ...clicks.map((k) => k.press));
      const ring = clicks.map((k) => k.ring).find((r) => r > 0 && r < 1) || 0;
      renderCursor(
        this.refs['ring'][0],
        this.refs['arrow'][0],
        MOTION.path(cursor.at, cursor.x, t),
        MOTION.path(cursor.at, cursor.y, t),
        press,
        ring,
      );
    }
  }
}

// ── Bausteine für Szenen-HTML ────────────────────────────────────────────

const FIELD = 'background: rgb(255 255 255 / .5); box-shadow: inset 0 4px 6px -3px rgb(47 76 121 / .23), inset 0 -4px 6px -3px rgb(255 255 255 / .90);';

/** Portal-Glasfläche 600×318 bei (20,18); mit `nav` die linke Navigation
 *  (Eintrag `nav` eingedrückt), Inhalt dann ab x = 180 (Panel-Koordinaten). */
export function panel(inner: string, nav: number | null = null): string {
  const items: [string, string][] = [['agent', 'KI-Agenten'], ['brain', 'Wissensbasis'], ['brush', 'Interface']];
  const navHtml =
    nav === null
      ? ''
      : items
          .map(
            ([ic, label], i) =>
              `<div style="${abs(14, 24 + i * 42, 140, 36, 'display:flex;')}">` +
              (i === nav ? `<div style="position:absolute;inset:0;border-radius:14px;${PRESSED}"></div>` : '') +
              `<div style="position:relative;flex:1;display:flex;align-items:center;gap:10px;padding:0 12px;font-size:14px;font-weight:${i === nav ? 600 : 500};color:${C.text};white-space:nowrap;">${icon(ic)}${label}</div></div>`,
          )
          .join('') + `<div style="${abs(164, 20, 2, 278, `background:${DIVIDER_V};`)}"></div>`;
  return glass(30, 0.42, 'left:20px;top:18px;width:600px;height:318px;', navHtml + inner);
}

/** Text an fester Stelle. */
export const text = (x: number, y: number, w: number, s: string, size = 14, weight = 500, color = C.text, extra = '') =>
  `<div style="${abs(x, y, w, size * 1.5, `font-size:${size}px;font-weight:${weight};color:${color};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;${extra}`)}">${s}</div>`;

export const hline = (x: number, y: number, w: number) => `<div style="${abs(x, y, w, 2, `background:${DIVIDER_H};`)}"></div>`;

/** Blaue Pille (Aktionsknopf). */
export const pill = (x: number, y: number, w: number, label: string, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, 32, `border-radius:999px;background:${C.action};color:#fff;display:flex;align-items:center;justify-content:center;gap:6px;font-size:13px;font-weight:600;box-shadow:0 6px 12px -6px rgb(37 99 235 / .6);transform-origin:50% 50%;`)}">${label}</div>`;

/** Helles Feld (Suche, Eingabe) als Pille. */
export const field = (x: number, y: number, w: number, label: string) =>
  `<div style="${abs(x, y, w, 34, `border-radius:999px;${FIELD}display:flex;align-items:center;gap:8px;padding:0 14px;box-sizing:border-box;font-size:13px;color:${C.muted};`)}">${label}</div>`;

/** Eingedrückte Auswahlfläche (CI: ausgewählt = vollflächig eingedrückt). */
export const pressed = (x: number, y: number, w: number, h: number, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, h, `border-radius:14px;${PRESSED}`)}"></div>`;

/** Grüner „Bereit“-Chip (Fläche #00A35C getönt, Text #00786F). */
export const okChip = (x: number, y: number, w: number, label: string, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, 24, `border-radius:999px;background:rgb(0 163 92 / .13);display:flex;align-items:center;justify-content:center;gap:6px;font-size:12px;font-weight:600;color:${C.okInk};white-space:nowrap;`)}"><span style="width:8px;height:8px;border-radius:999px;background:${C.ok};"></span>${label}</div>`;

/** Grauer Chip. */
export const chip = (x: number, y: number, w: number, label: string, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, 24, `border-radius:999px;background:rgb(83 109 145 / .12);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:600;color:${C.muted};white-space:nowrap;`)}">${label}</div>`;

/** Fortschrittsbalken; die Füllung (`ref`) wird über `s`/Ursprung links gestreckt. */
export const bar = (x: number, y: number, w: number, ref: string) =>
  `<div style="${abs(x, y, w, 4, 'border-radius:999px;background:rgb(83 109 145 / .16);overflow:hidden;')}"><div data-r="${ref}" style="width:100%;height:100%;border-radius:999px;background:${C.accent};transform-origin:0 50%;transform:scale(0);"></div></div>`;

/** Chatblase: `me` = rechts, blau; sonst links, hell. */
export const bubble = (x: number, y: number, w: number, s: string, me: boolean, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, 0, `height:auto;padding:8px 12px;border-radius:14px;font-size:12.5px;line-height:1.4;box-sizing:border-box;${me ? `background:${C.action};color:#fff;border-bottom-right-radius:4px;` : `background:rgb(255 255 255 / .75);color:${C.text};border-bottom-left-radius:4px;box-shadow:0 2px 6px -3px rgb(38 65 112 / .25);`}`)}">${s}</div>`;

/** Kleine Checkbox; `ref` = Häkchen-Ebene. */
export const checkbox = (x: number, y: number, ref: string) =>
  `<div style="${abs(x, y, 18, 18, `border-radius:5px;background:rgb(255 255 255 / .6);box-shadow:inset 0 0 0 1.5px rgb(83 109 145 / .45);`)}">` +
  `<div data-r="${ref}" style="position:absolute;inset:0;border-radius:5px;background:${C.action};display:flex;align-items:center;justify-content:center;opacity:0;">${icon('check', 13, '#fff', 2.6)}</div></div>`;

/** Schalter (CI: an = Blau, aus = Grau); `ref` = An-Ebene, `ref + 'Knob'` = Knopf. */
export const toggle = (x: number, y: number, ref: string) =>
  `<div style="${abs(x, y, 38, 22, 'border-radius:999px;background:rgb(83 109 145 / .32);')}">` +
  `<div data-r="${ref}" style="position:absolute;inset:0;border-radius:999px;background:${C.action};opacity:0;"></div>` +
  `<div data-r="${ref}Knob" style="${abs(3, 3, 16, 16, 'border-radius:999px;background:#fff;box-shadow:0 1px 3px rgb(38 65 112 / .35);')}"></div></div>`;

/** Orbi (3D, schräge Augen — `scene-kit` `orbi3d`). */
export const orbi = (x: number, y: number, size = 30) => `<div style="${abs(x, y, size, size)}">${orbi3d(size)}</div>`;

export { abs, C, icon, glass };
